import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Award, BookOpen, Heart, Users } from 'lucide-react';

const About = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  const backgroundImage = "/img/about/about-bg.jpg"

  const qualifications = [
    {
      icon: Award,
      title: "Diplôme d'État",
      description: "Sage-femme diplômée de l'École de Sages-femmes en 2010"
    },
    {
      icon: BookOpen,
      title: "Formation aux risques",
      description: "Situation critique en maternité, Réanimation du nouveau-né, Rythme cardiaque fœtal"
    },
    {
      icon: Heart,
      title: "Formation physiologique",
      description: "Allaitement, Position accouchements (méthode de Gasquet), naissance physiologique"
    },
    {
      icon: Users,
      title: "Suivi des patientes",
      description: "Diabète gestationnel, activités aquatiques pré-natales, IVG médicamenteuse"
    }
  ];

  return (
    <section id="about" className="py-20 lg:py-32 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-sage-900/80 z-10"></div>
        <img
          src={backgroundImage}
          alt="Silhouette grossesse"
          loading="lazy"
          className="w-full h-full object-cover opacity-30"
        />
      </div>

      <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <motion.div
            ref={ref}
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div>
              <motion.h2
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="font-display text-4xl lg:text-5xl font-bold text-white mb-6"
              >
                Qui suis-je ?
              </motion.h2>
              
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="space-y-6 text-warm-100 leading-relaxed"
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-6">
                  <img
                    src="/img/about/angele pro.jpg"
                    alt="Portrait d'Angèle Rosalie, sage-femme libérale à Annecy"
                    loading="lazy"
                    width="320"
                    height="320"
                    className="w-40 h-40 sm:w-52 sm:h-52 rounded-2xl object-cover object-[center_45%] shadow-lg ring-1 ring-white/30 flex-shrink-0 mx-auto sm:mx-0"
                  />
                  <p className="text-lg">
                    Je suis Angèle Rosalie, Sage-femme passionnée depuis 15 ans,
                    j'ai exercé mon métier en hospitalier et en libéral.
                    Je propose désormais un accompagnement global et personnalisé 
                    pour les femmes et les couples souhaitant vivre un accouchement naturel en toute sérénité.
                  </p>
                </div>
                
                <blockquote className="text-lg italic text-warm-50">
                  « 
                  Ce qui m’a naturellement amenée vers le plateau technique, c’est une conviction profonde : la personne qui nous accompagne le jour de la naissance a une place essentielle dans cette expérience.

Pouvoir choisir cette personne, la connaître, créer un lien de confiance au fil de la grossesse et savoir qu’elle sera là pour vous accompagner le jour J, je suis convaincue que cela peut faire toute la différence.

C’est cette relation de confiance, cette continuité et cette présence bienveillante qui donnent tout son sens à l’accompagnement en plateau technique »
                </blockquote>
                <div className="text-sm">
                    <p>Les accouchements ont lieu sur le plateau technique du : </p>
                    <p> <strong> Centre Hospitalier Annecy Genevois </strong>.</p>
                    <p>Adresse: 1 Av. de l’Hôpital, 74370 Épagny Metz-Tessy — Standard: 04 50 63 63 63</p>
                  </div>
                
                <div className="flex flex-col sm:flex-row sm:items-center gap-6">
                <p className="text-lg">
                  Je travaille en équipe avec <strong>Adrien Vallet</strong> dont le cabinet se trouve à Pringy.
                </p>
  <img
    src="/img/about/Andre.JPG"
    alt="Portrait d'Adrien Vallet, sage-femme"
    loading="lazy"
    width="96"
    height="96"
    className="w-40 h-40 sm:w-52 sm:h-52 rounded-2xl object-cover object-[center_45%] shadow-lg ring-1 ring-white/30 flex-shrink-0 mx-auto sm:mx-0"
  />
  
                </div>
                
                <p className="text-lg">
                  Je respecte les protocoles du réseau périnatal des 2 Savoies 
                  et je suis <strong>adhérente de l’organisation nationale syndicale des sages-femmes</strong>.
                </p>
              </motion.div>
            </div>

            {/* CTA retirés selon la demande */}
          </motion.div>

          {/* Right Content - Qualifications */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="space-y-6"
          >
            <div className="glass-card p-8 rounded-3xl">
              <h3 className="font-display text-2xl font-semibold text-sage-800 mb-8">
                Mes qualifications
              </h3>
              
              <div className="grid gap-6">
                {qualifications.map((qual, index) => {
                  const Icon = qual.icon;
                  return (
                    <motion.div
                      key={qual.title}
                      initial={{ opacity: 0, y: 20 }}
                      animate={isInView ? { opacity: 1, y: 0 } : {}}
                      transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
                      whileHover={{ scale: 1.02 }}
                      className="flex items-start space-x-4 p-4 rounded-2xl hover:bg-warm-50/50 transition-all duration-300"
                    >
                      <div className="bg-primary-100 p-3 rounded-full flex-shrink-0 mt-1">
                        <Icon className="w-6 h-6 text-primary-600" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-sage-800 mb-2">
                          {qual.title}
                        </h4>
                        <p className="text-sage-600 text-sm leading-relaxed">
                          {qual.description}
                        </p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>

            {/* Philosophy Card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 1.2 }}
              className="glass-card p-8 rounded-3xl"
            >
              <h4 className="font-display text-xl font-semibold text-sage-800 mb-4">
                Ma philosophie
              </h4>
              <blockquote className="text-sage-700 italic leading-relaxed">
                "Chaque naissance est unique et mérite d'être accompagnée avec respect, 
                bienveillance et expertise. Mon rôle est de vous guider en toute confiance 
                vers cette rencontre extraordinaire avec votre enfant."
              </blockquote>
              <cite className="block mt-4 text-primary-600 font-medium">
                — Angèle Rosalie, Sage-femme
              </cite>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
